import React, { useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { connect } from 'react-redux'
import Header from '../../Header'
import { Numpad } from '../../Numpad'
import Footer from '../../Footer'
import { FormField } from '../../FormField'
import { setUIBackwardsAction, setUIForwardAction } from '../../../actions'
import { UI } from '../../../constants/ui'
import { setCargoWeightAction } from '../../../actions/reservationChange'
import { AppState } from '../../../reducers'
import './cargoWeight.less'
import cargoWeightImg from '../../../assets/cargo-weight.png'
import CustomCaret, { CustomCaretProps } from '../../CustomCaret'
import FooterMobile from '../../FooterMobile'
import { getMode } from '../../../hooks/mode'
import { MODE } from '../../../constants/mode'
import { validateKeyboardInput } from '../../../utilities/inputValidation'

const mapDispatchToProps = {
  setUIForward: setUIForwardAction,
  setUIBackwards: setUIBackwardsAction,
  setCargoWeight: setCargoWeightAction,
}
type DispatchProps = typeof mapDispatchToProps

const mapStateToProps = (state: AppState) => ({
  cargoWeight: state.reservationChange.cargoWeight,
})

type StateProps = ReturnType<typeof mapStateToProps>
type Props = DispatchProps & StateProps

interface CustomMobileProps {
  clearInputField: (currentInput: any) => void
  cargoWeightInput: React.RefObject<HTMLInputElement>
  onChange: (value: string) => void
  onKeyboardDown: (event: React.KeyboardEvent<HTMLInputElement>) => void
  onSubmit: () => void
  isCargoWeightWrongTyping: boolean
}

type CargoWeightMobileProps = CustomMobileProps & Omit<Props, 'setCargoWeight' | 'setUIForward'>

const CargoWeightMobile: React.FC<CargoWeightMobileProps> = ({ cargoWeight, clearInputField, cargoWeightInput, onChange, setUIBackwards, onKeyboardDown, onSubmit, isCargoWeightWrongTyping }) => {
  const { t } = useTranslation()

  return (
    <div className="cargo-weight-mobile">
      <div className="cargo-weight-main">
        <div className="cargo-weight-title" dangerouslySetInnerHTML={{ __html: t('cargo') }}></div>
        <div className="img-cargo-weight-container">
          <img className="img-cargo-weight" src={cargoWeightImg} alt="cargo weight" />
        </div>
        <div className="cargo-weight-info">
          <FormField
            id="cargo-weight"
            label={t('weightKg')}
            isExtendedClearNeed={true}
            clearInputField={clearInputField}
            currentInput={cargoWeightInput.current}
            sublabel={isCargoWeightWrongTyping ? t('typeNumbersOnly') : ''}
            isError={isCargoWeightWrongTyping}
          >
            <div className="input-cont">
              <div className="measurement">{t('kilogram')}</div>
              <input
                spellCheck={false}
                inputMode='numeric'
                ref={cargoWeightInput}
                value={+cargoWeight === 0 ? '' : cargoWeight}
                className="input-field-mobile"
                onChange={(e) => onChange(e.target.value)}
                onKeyDown={onKeyboardDown}
              />
            </div>
          </FormField>
        </div>
        <button className="main-button-mobile" onClick={onSubmit} disabled={+cargoWeight === 0}>
          {t('next')}
        </button>
      </div>
      <div className="footer-mobile-container">
        <FooterMobile showPageCount back={() => setUIBackwards(UI.CARGO_CONTENT)} />
      </div>
    </div>
  )
}

interface CustomDesktopProps {
  clearInputField: (currentInput: any) => void
  cargoWeightInput: React.RefObject<HTMLInputElement>
  onChange: (value: string) => void
  updateCaretPosition: () => void
  refCargoWeightCaret: React.Ref<any>
  activeInput: HTMLInputElement
  mode: MODE
  onKeyboardDown: (event: React.KeyboardEvent<HTMLInputElement>) => void
  onSubmit: () => void
  isCargoWeightWrongTyping: boolean
}

type CargoWeightDesktopProps = CustomDesktopProps & Omit<Props, 'setCargoWeight' | 'setUIForward'>

const CargoWeightDesktop: React.FC<CargoWeightDesktopProps> = ({ setUIBackwards, cargoWeight, clearInputField, cargoWeightInput, onChange, updateCaretPosition, refCargoWeightCaret, activeInput, mode, onKeyboardDown, onSubmit, isCargoWeightWrongTyping }) => {
  const { t } = useTranslation()
  const isKioskMode = mode === MODE.KIOSK
  return (
    <>
      <Header showPageCount back={() => setUIBackwards(UI.CARGO_CONTENT)} />
      <div className="cargo-weight">
        <div className={`cargo-weight-main${!isKioskMode ? ' cargo-weight-main-desktop' : ''}`}>
          <div className="cargo-weight-title" dangerouslySetInnerHTML={{ __html: t('cargo') }}></div>
          <div className="cargo-weight-info">
            <FormField
              id="cargo-weight"
              label={t('weightKg')}
              isExtendedClearNeed={!!cargoWeight}
              clearInputField={clearInputField}
              currentInput={cargoWeightInput.current}
              sublabel={isCargoWeightWrongTyping ? t('typeNumbersOnly') : ''}
              isError={isCargoWeightWrongTyping}
            >
              <div className="input-cont">
                <div className="measurement">{t('kilogram')}</div>
                <input
                  autoFocus
                  spellCheck={false}
                  ref={cargoWeightInput}
                  value={+cargoWeight === 0 ? '' : cargoWeight}
                  className="input-field"
                  onChange={(e) => onChange(e.target.value)}
                  onClick={updateCaretPosition}
                  onKeyDown={onKeyboardDown}
                />
                {isKioskMode && <CustomCaret ref={refCargoWeightCaret} inputRef={cargoWeightInput.current} />}
              </div>
            </FormField>
            <button className="main-button" onClick={onSubmit} disabled={+cargoWeight === 0}>
              {t('next')}
            </button>
          </div>
        </div>
        {isKioskMode && <div className="numpad-container">
          <div className="img-cargo-weight-container">
            <img className="img-cargo-weight" src={cargoWeightImg} alt="cargo weight" />
          </div>
          <Numpad input={activeInput} onChange={onChange} />
        </div>}
      </div>
      <Footer />
    </>
  )
}

const CargoWeight: React.FC<Props> = ({ setUIForward, setUIBackwards, setCargoWeight, cargoWeight }: Props) => {
  const cargoWeightInput = useRef<HTMLInputElement | null>(null)
  const refCargoWeightCaret = useRef<CustomCaretProps>(null)
  const [activeInput, setActiveInput] = useState<any>()
  const [isCargoWeightWrongTyping, setCargoWeightWrongTyping] = useState(false)
  const mode = getMode()

  const updateCaretPosition = () => {
    refCargoWeightCaret.current && refCargoWeightCaret.current.updateCaretPosition()
  }

  const onChange = (value: string) => {
    const isValid = validateKeyboardInput(value, { numeric: true })
    if (!isValid) {
      return setCargoWeightWrongTyping(true)
    }
    setCargoWeightWrongTyping(false)
    if (value.length <= 14) {
      setCargoWeight(parseInt(value) || 0)
    } else {
      activeInput && (activeInput.value = cargoWeight)
    }
    updateCaretPosition()
  }
  const clearInputField = (currentInput: any) => {
    setCargoWeightWrongTyping(false)
    setActiveInput(currentInput)
    setCargoWeight(0)
    if (mode !== MODE.KIOSK && cargoWeightInput && cargoWeightInput.current) {
      cargoWeightInput.current.focus()
    }
  }

  const onSubmit = () => {
    setUIForward(UI.FROM_TO)
  }

  const onKeyboardDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.keyCode === 13 && +cargoWeight !== 0) {
      onSubmit()
    }
  }

  useEffect(() => {
    setActiveInput(cargoWeightInput.current)
  }, [])

  if (mode === MODE.MOBILE) {
    const mobileProps = { cargoWeight, clearInputField, cargoWeightInput, onChange, setUIBackwards, onKeyboardDown, onSubmit, isCargoWeightWrongTyping }
    return <CargoWeightMobile {...mobileProps} />
  } else {
    const desktopProps = { setUIBackwards, cargoWeight, clearInputField, cargoWeightInput, onChange, updateCaretPosition, refCargoWeightCaret, activeInput, mode, onKeyboardDown, onSubmit, isCargoWeightWrongTyping }
    return <CargoWeightDesktop {...desktopProps} />
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(CargoWeight)
